// =============================================================================
// SIREPPAR - Página de Detalle de Partido
// =============================================================================

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partidoService, interaccionService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { puedeEscribirInteracciones } from '../../utils/permisosInteracciones';
import Modal from '../../components/common/Modal';
import toast from 'react-hot-toast';
import { useParams, Link, Navigate, useNavigate, useSearchParams, useLocation } from 'react-router-dom';

const ROLES_INTERNOS = ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'MESA_ENTRADAS', 'ANALISTA', 'AUDITOR'];

interface Partido {
  id: string;
  numero: number;
  denominacion: string;
  sigla: string;
  nota?: string;
  tipo: string;
  estado: string;
  fechaConstitucion?: string;
  fechaReconocimiento?: string;
  fechaUltimoReempadronamiento?: string;
  resolucionReconocimiento?: string;
  domicilioLegal?: string;
  localidad?: string;
  codigoPostal?: string;
  emailOficial?: string;
  sitioWeb?: string;
  telefonoOficial?: string;
  autoridades?: Array<{
    id: string;
    dni: string;
    nombre: string;
    apellido: string;
    mandatos?: Array<{
      cargo: string;
      esPresidente: boolean;
      mandatoDesde: string;
      activo: boolean;
    }>;
  }>;
  _count?: { afiliaciones: number; expedientes: number; sanciones: number };
}

const estadoConfig: Record<string, { color: string; bg: string; label: string }> = {
  // BORRADOR: trámite sin presentar. Sin esta entrada caía en el fallback y se
  // mostraba como "En Formación", que es una situación distinta (ya presentado).
  BORRADOR: { color: 'text-gray-700', bg: 'bg-gray-100', label: 'Borrador' },
  EN_FORMACION: { color: 'text-yellow-800', bg: 'bg-yellow-100', label: 'En Formación' },
  PENDIENTE_REVISION: { color: 'text-blue-800', bg: 'bg-blue-100', label: 'En revision' },
  RECONOCIDO: { color: 'text-blue-800', bg: 'bg-blue-100', label: 'Reconocido' },
  VIGENTE: { color: 'text-green-800', bg: 'bg-green-100', label: 'Vigente' },
  SUSPENDIDO: { color: 'text-orange-800', bg: 'bg-orange-100', label: 'Suspendido' },
  CADUCO: { color: 'text-red-800', bg: 'bg-red-100', label: 'Caduco' },
  EXTINGUIDO: { color: 'text-gray-800', bg: 'bg-gray-100', label: 'Extinguido' },
  FUSIONADO: { color: 'text-purple-800', bg: 'bg-purple-100', label: 'Fusionado' },
  ABSORBIDO: { color: 'text-purple-800', bg: 'bg-purple-100', label: 'Absorbido' },
};

export default function PartidoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE'].includes(user?.rol || '');
  const isInternal = ROLES_INTERNOS.includes(user?.rol || '');
  const navigate = useNavigate();
  const location = useLocation();
  // Si se llegó desde otra pantalla (ej. Candidaturas Gaiman) que pidió volver
  // a un lugar específico, se respeta; si no, el comportamiento de siempre.
  const backHref = (location.state as { from?: string } | null)?.from || (isInternal ? '/partidos' : '/mi-partido');

  // Roles externos solo pueden ver su propio partido
  if (!isInternal && user?.partidoId && user.partidoId !== id) {
    return <Navigate to="/unauthorized" replace />;
  }
  
  const [showCambioEstadoModal, setShowCambioEstadoModal] = useState(false);
  // Permite abrir el modal directo desde otra página: /partidos/:id?cambiarEstado=1
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('cambiarEstado') === '1') setShowCambioEstadoModal(true);
  }, [searchParams]);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [motivoCambio, setMotivoCambio] = useState('');
  const [resolucionCambio, setResolucionCambio] = useState('');

  const { data: partido, isLoading, error } = useQuery({
    queryKey: ['partido', id],
    queryFn: async () => {
      const response = await partidoService.getById(id!, 'all');
      return response.data.data as Partido;
    },
    enabled: !!id,
  });

  const { data: requisitos } = useQuery({
    queryKey: ['partido-requisitos', id],
    queryFn: async () => {
      const response = await partidoService.verificarRequisitos(id!);
      return response.data.data;
    },
    enabled: !!id && partido?.estado === 'EN_FORMACION',
  });

  const { data: interaccionesData, isLoading: loadingInteracciones } = useQuery({
    queryKey: ['interacciones-partido', id],
    queryFn: () => interaccionService.list({ partidoId: id!, pageSize: '10' }),
    enabled: !!id && isInternal,
  });
  const interacciones = interaccionesData?.data ?? [];

  const cambioEstadoMutation = useMutation({
    mutationFn: () => partidoService.cambiarEstado(id!, { estado: nuevoEstado, motivo: motivoCambio, resolucion: resolucionCambio || undefined }),
    onSuccess: () => {
      toast.success('Estado actualizado correctamente');
      queryClient.invalidateQueries({ queryKey: ['partido', id] });
      setShowCambioEstadoModal(false);
      setNuevoEstado(''); setMotivoCambio(''); setResolucionCambio('');
    },
    onError: (error: any) => toast.error(error.response?.data?.error?.message || 'Error al cambiar estado'),
  });

  const formatDate = (date?: string) => date ? new Date(date).toLocaleDateString('es-AR') : '-';

  if (isLoading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" /></div>;
  if (error || !partido) return <div className="text-center py-12"><h3 className="text-lg font-medium text-gray-900">Partido no encontrado</h3><Link to={backHref} className="mt-4 text-primary-600">Volver al listado</Link></div>;

  const estadoInfo = estadoConfig[partido.estado] || estadoConfig.EN_FORMACION;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to={backHref} className="text-gray-500 hover:text-gray-700">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          </Link>
          <div>
            <p className="text-sm text-gray-500">Partido Nº {partido.numero}</p>
            <h1 className="text-2xl font-bold text-gray-900">{partido.denominacion}</h1>
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-3">
            <button onClick={() => setShowCambioEstadoModal(true)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cambiar Estado</button>
            <Link to={`/partidos/${id}/editar`} className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Editar</Link>
          </div>
        )}
      </div>

      {/* Requisitos para reconocimiento */}
      {partido.estado === 'EN_FORMACION' && requisitos && (
        <div className={`rounded-lg p-4 ${requisitos.cumple ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <h3 className={`font-medium ${requisitos.cumple ? 'text-green-800' : 'text-yellow-800'}`}>
            {requisitos.cumple ? '✓ Cumple requisitos para reconocimiento' : '⚠ Requisitos pendientes para reconocimiento'}
          </h3>
          <ul className="mt-2 space-y-1">
            {requisitos.requisitos.map((req: any, i: number) => (
              <li key={i} className={`text-sm ${req.cumple ? 'text-green-700' : 'text-yellow-700'}`}>
                {req.cumple ? '✓' : '○'} {req.nombre} {req.detalle && `(${req.detalle})`}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Información General */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200"><h2 className="text-lg font-medium text-gray-900">Información General</h2></div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><span className="text-sm text-gray-500">Denominación</span><p className="font-medium">{partido.denominacion}</p></div>
              <div><span className="text-sm text-gray-500">Sigla</span><p className="font-medium">{partido.sigla}</p></div>
              <div><span className="text-sm text-gray-500">Tipo</span><p className="font-medium">{partido.tipo}</p></div>
              <div><span className="text-sm text-gray-500">Estado</span><p><span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${estadoInfo.bg} ${estadoInfo.color}`}>{estadoInfo.label}</span></p></div>
              <div><span className="text-sm text-gray-500">Fecha Constitución</span><p className="font-medium">{formatDate(partido.fechaConstitucion)}</p></div>
              <div><span className="text-sm text-gray-500">Fecha Reconocimiento</span><p className="font-medium">{formatDate(partido.fechaReconocimiento)}</p></div>
              {partido.nota && (
                <div className="sm:col-span-2"><span className="text-sm text-gray-500">Nota interna</span><p className="font-medium whitespace-pre-wrap break-words">{partido.nota}</p></div>
              )}
              {partido.resolucionReconocimiento && <div><span className="text-sm text-gray-500">Resolución</span><p className="font-medium">{partido.resolucionReconocimiento}</p></div>}
              <div><span className="text-sm text-gray-500">Último Reempadronamiento</span><p className="font-medium">{formatDate(partido.fechaUltimoReempadronamiento)}</p></div>
            </div>
          </div>

          {/* Autoridades */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">Autoridades</h2>
              {isAdmin && <Link to={`/partidos/${id}/autoridades`} className="text-sm text-primary-600 hover:text-primary-700">Gestionar</Link>}
            </div>
            <div className="divide-y divide-gray-200">
              {partido.autoridades?.length ? partido.autoridades.map((a) => (
                <div key={a.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium">{a.nombre[0]}{a.apellido[0]}</div>
                    <div>
                      <p className="font-medium text-gray-900">{a.nombre} {a.apellido}</p>
                      <p className="text-sm text-gray-500">
                        {a.mandatos?.[0]?.cargo}
                        {a.mandatos?.[0]?.esPresidente && ' • Presidente'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <p>DNI: {a.dni}</p>
                    <p>Desde: {formatDate(a.mandatos?.[0]?.mandatoDesde || '')}</p>
                  </div>
                </div>
              )) : <div className="p-6 text-center text-gray-500">No hay autoridades registradas</div>}
            </div>
          </div>

          {/* Contacto */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200"><h2 className="text-lg font-medium text-gray-900">Domicilio y Contacto</h2></div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><span className="text-sm text-gray-500">Domicilio Legal</span><p className="font-medium">{partido.domicilioLegal || '-'}</p></div>
              <div><span className="text-sm text-gray-500">Localidad</span><p className="font-medium">{partido.localidad || '-'}</p></div>
              <div><span className="text-sm text-gray-500">Código Postal</span><p className="font-medium">{partido.codigoPostal || '-'}</p></div>
              <div><span className="text-sm text-gray-500">Domicilio Electrónico</span><p className="font-medium">{partido.emailOficial || '-'}</p></div>
              <div><span className="text-sm text-gray-500">Teléfono</span><p className="font-medium">{partido.telefonoOficial || '-'}</p></div>
              {partido.sitioWeb && <div><span className="text-sm text-gray-500">Sitio Web</span><p className="font-medium">{partido.sitioWeb}</p></div>}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Estadísticas</h3>
            <div className="space-y-4">
              <div className="flex justify-between"><span className="text-gray-600">Afiliados</span><span className="text-2xl font-bold">{partido._count?.afiliaciones?.toLocaleString() || 0}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Expedientes</span><span className="text-2xl font-bold">{partido._count?.expedientes || 0}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Sanciones Activas</span><span className="text-2xl font-bold text-orange-600">{partido._count?.sanciones || 0}</span></div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Acciones Rápidas</h3>
            <div className="space-y-2">
              <Link to={`/expedientes?partidoId=${id}`} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">Ver expedientes</Link>
              <Link to={`/documentos?partidoId=${id}`} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">Ver documentos</Link>
              {isAdmin && <Link to={`/expedientes/nuevo?partidoId=${id}`} className="block px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg">Nuevo expediente</Link>}
            </div>
          </div>
        </div>
      </div>
      {/* Interacciones */}
      {isInternal && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">
              Interacciones
              {interacciones.length > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500">{interacciones.length} registradas</span>
              )}
            </h2>
            {puedeEscribirInteracciones(user?.rol) && (
            <button
              onClick={() => navigate(`/interacciones/nueva?partidoId=${id}`)}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium"
            >
              + Nueva interacción
            </button>
            )}
          </div>

          {loadingInteracciones ? (
            <div className="p-6 text-center text-gray-400 text-sm">Cargando...</div>
          ) : interacciones.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">No hay interacciones registradas para este partido.</div>
          ) : (
            <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contacto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resumen</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {interacciones.map((i: any) => (
                  <tr
                    key={i.id}
                    onClick={() => navigate(`/interacciones/${i.id}`)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {new Date(i.fechaInteraccion).toLocaleDateString('es-AR')}
                      <div className="text-xs text-gray-400">{new Date(i.fechaInteraccion).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} hs</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{i.tipo}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {i.contactoNombre}
                      {i.contactoCargo && <div className="text-xs text-gray-400">{i.contactoCargo}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-xs truncate">{i.resumen}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${i.estado === 'RESUELTO' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {i.estado === 'RESUELTO' ? 'Resuelto' : 'Pendiente'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table> </div>
          )}

          {interacciones.length >= 10 && (
            <div className="px-6 py-3 border-t border-gray-100 text-right">
              <button onClick={() => navigate(`/interacciones?partidoId=${id}`)} className="text-sm text-blue-600 hover:underline">
                Ver todas →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      <Modal isOpen={showCambioEstadoModal} onClose={() => setShowCambioEstadoModal(false)} title="Cambiar Estado" size="md">
        <form onSubmit={(e) => { e.preventDefault(); cambioEstadoMutation.mutate(); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nuevo Estado</label>
            <select value={nuevoEstado} onChange={(e) => setNuevoEstado(e.target.value)} required className="w-full border rounded-lg px-3 py-2">
              <option value="">Seleccione</option>
              <option value="RECONOCIDO">Reconocido</option>
              <option value="VIGENTE">Vigente</option>
              <option value="SUSPENDIDO">Suspendido</option>
              <option value="CADUCO">Caduco</option>
              <option value="EXTINGUIDO">Extinguido</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
            <textarea value={motivoCambio} onChange={(e) => setMotivoCambio(e.target.value)} required rows={3} className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Resolución (opcional)</label>
            <input type="text" value={resolucionCambio} onChange={(e) => setResolucionCambio(e.target.value)} className="w-full border rounded-lg px-3 py-2" placeholder="RES-001/2024" />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setShowCambioEstadoModal(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
            <button type="submit" disabled={cambioEstadoMutation.isPending} className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">
              {cambioEstadoMutation.isPending ? 'Guardando...' : 'Confirmar'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
